import React from 'react';

import { Icon } from 'ui';

type Props = {
  error?: boolean;
  warning?: boolean;
  success?: boolean;
  info?: boolean;
  heading?: React.ReactNode;
  border?: boolean;
  children: React.ReactNode;
  icon?: UIIconName;
  displayIcon?: boolean;
};

type Color = 'red' | 'yellow' | 'green' | 'blue';

const getBackgroundColor = (color: Color) => {
  switch (color) {
    case 'red':
      return 'bg-red-50';
    case 'yellow':
      return 'bg-yellow-50';
    case 'green':
      return 'bg-green-50';
    case 'blue':
      return 'bg-blue-50';
    default:
      return 'bg-blue-50';
  }
};

const getHeadingTextColor = (color: Color) => {
  switch (color) {
    case 'red':
      return 'text-red-800';
    case 'yellow':
      return 'text-yellow-800';
    case 'green':
      return 'text-green-800';
    case 'blue':
      return 'text-blue-800';
    default:
      return 'text-blue-800';
  }
};

const getTextColor = (color: Color) => {
  switch (color) {
    case 'red':
      return 'text-red-700';
    case 'yellow':
      return 'text-yellow-700';
    case 'green':
      return 'text-green-700';
    case 'blue':
      return 'text-blue-700';
    default:
      return 'text-blue-700';
  }
};

const getBorderColor = (color: Color) => {
  switch (color) {
    case 'red':
      return 'border-red-400';
    case 'yellow':
      return 'border-yellow-400';
    case 'green':
      return 'border-green-400';
    case 'blue':
      return 'border-blue-400';
    default:
      return 'border-blue-400';
  }
};

function Message({
  error = false,
  warning = false,
  success = false,
  info = false,
  border = false,
  heading,
  children,
  icon: iconOverride,
  displayIcon = true,
}: Props) {
  let color: Color;
  let icon: UIIconName;

  const iconContainerClasses = ['flex-shrink-0'];
  const containerClasses = ['rounded-md', 'p-4'];
  const contentClasses = ['w-full'];
  const headingClasses = ['text-sm', 'font-medium', 'mb-2'];
  const childrenClasses = ['text-sm'];

  if (error) {
    color = 'red';
    icon = 'x-circle';
    iconContainerClasses.push('-mt-1');
  } else if (warning) {
    color = 'yellow';
    icon = 'exclamation';
    iconContainerClasses.push('-mt-0.5');
  } else if (success) {
    color = 'green';
    icon = 'check-circle';
    iconContainerClasses.push('-mt-1');
  } else {
    color = 'blue';
    icon = 'information-circle';
    iconContainerClasses.push('-mt-1');
  }
  if (iconOverride) {
    icon = iconOverride;
  }
  containerClasses.push(getBackgroundColor(color));
  if (border) {
    containerClasses.push(`border`);
    containerClasses.push(getBorderColor(color));
  }

  headingClasses.push(getHeadingTextColor(color));
  childrenClasses.push(getTextColor(color));

  return (
    <div className={containerClasses.join(' ')}>
      <div className="flex gap-1">
        {displayIcon && (
          <div className={iconContainerClasses.join(' ')}>
            <Icon name={icon} color={color} fitted />
          </div>
        )}
        <div className={contentClasses.join(' ')}>
          {heading && <h3 className={headingClasses.join(' ')}>{heading}</h3>}
          <div className={childrenClasses.join(' ')}>{children}</div>
        </div>
      </div>
    </div>
  );
}

export default Message;
